<template>
    <div class="relative">
        <AppButton
            @click="toggleIsDropdownVisible"
            aria-expanded="false"
            :class="{
                '!bg-gray-tier-6': isDropdownVisible,
            }"
            icon="PushPinOutlined"
            icon-size="large"
            icon-class="rotate-45"
            text="Pin"
            icon-only="true"
            class="px-3"
        >
            
        </AppButton>
        <div
            v-if="isDropdownVisible"
            v-click-outside="closeDropdown"
            class="
                absolute 
                right-0
                rounded-md 
                top-full  
                translate-y-[15px] 
                w-[calc(var(--app-detail-aside-width)_-_4rem)] 
                z-10
                dark:bg-v2-gray-50
                bg-v2-white
                border border-gray-225 dark:border-gray-850 shadow-black/20 shadow-xl
            "
        >
            <div class="flex flex-col max-h-[40vh]">
                <ShadowScrollContainer>
                    <ul class="space-y-2 px-5 py-2">
                        <TrayButton
                            v-for="tray in drawer.trays"
                            :key="tray.id"
                            :tray="tray"
                            :id="id"
                            type="item"
                            :current-pin="currentPin"
                            @toggle-pin="updateCurrentPin"
                        />
                    </ul>
                </ShadowScrollContainer>
            </div>
            <div class="p-5 border-t border-gray-225 dark:border-gray-850">
                <button
                v-if="!isFormVisible"
                @click="toggleIsFormVisible"
                :aria-expanded="isFormVisible"
                class="flex font-medium items-center gap-2 text-v2-gray-20 text-sm dark:text-v2-white"
                >
                    <BaseSvg name="Add" aria-hidden="true" class="bg-v2-green border border-v2-green fill-v2-white rounded size-6 dark:bg-transparent dark:fill-v2-green" />
                    Create new Tray
                </button>
            </div>
            <form v-if="isFormVisible" class="bg-v2-white flex gap-3 p-3 rounded dark:bg-v2-gray-60 dark:border dark:border-v2-gray-10">
                <span aria-hidden="true" class="bg-v2-gray-130 flex items-center justify-center rounded-md shrink-0 size-10 dark:bg-v2-gray-50">
                    <span class="font-medium text-[12px] text-v2-gray-20 uppercase dark:text-v2-gray-90">New</span>
                </span>
                <div class="grow">
                    <fieldset class="flex flex-col gap-2">
                        <label for="tray-name" class="sr-only">Tray name</label>
                        <input
                            v-model="trayNameInput"
                            type="text"
                            name="tray-name"
                            id="tray-name"
                            ref="focusInput"
                            class="border-blue rounded dark:bg-v2-gray-40"
                            placeholder="Tray name"
                        />
                        <label for="tray-description" class="sr-only">Tray description (optional)</label>
                        <textarea
                            v-model="trayDescriptionInput"
                            name="tray-description"
                            id="tray-description"
                            class="border-v2-gray-110 rounded dark:bg-v2-gray-40 dark:border-v2-gray-10"
                            placeholder="Tray description (optional)"
                        ></textarea>
                    </fieldset>
                    <div class="mt-2 text-right flex justify-end gap-2">
                        <AppButton
                            @click.prevent="toggleIsFormVisible"
                            theme="default"
                            text="Cancel"
                            :disabled="isLoading"
                        />
                        <button
                            @click.prevent="submitCreateTray"
                            type="button"
                            :disabled="!trayNameInput.trim()?.length"
                            class="
                                disabled:opacity-50
                                disabled:pointer-events-none 
                                btn-app-icon-black-on-gray
                                leading-none 
                                px-4 
                                py-2 
                                rounded 
                                text-sm 
                                flex
                                items-center
                                gap-2
                            "
                        >
                            <LoadingCircle v-if="isLoading" />
                            <span>Create</span>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import {nextTick, onMounted, onUnmounted, ref, inject} from "vue";
import {AppButton, LoadingCircle, TrayButton} from "@/Components";
import ShadowScrollContainer from "@/Components/Modules/ShadowScrollContainer.vue";
import {eventBus, usePinsDrawer} from "@/Composables";
import axios from 'axios';

const props = defineProps(['id','type']);

const route = inject('route');
const isDropdownVisible = ref(false);
const isFormVisible = ref(false);
const trayNameInput = ref('');
const trayDescriptionInput = ref('')
const currentPin = ref(null);

const { drawer, createTray, fetchTrays, isLoading } = usePinsDrawer();

function toggleIsDropdownVisible() {
    isDropdownVisible.value = !isDropdownVisible.value;
    if (isDropdownVisible.value) {
        fetchCurrentPin();
    }
}

function fetchCurrentPin() {
    if (props.id && props.type) {
        axios.get(route(`web.account.pins.${props.type}`, props.id))
            .then(response => {
                currentPin.value = response.data;
            })
            .catch(error => {
                console.log('Error fetching current pin:', error);
                currentPin.value = null;
            });
    }
}

function updateCurrentPin(pinData) {
    currentPin.value = pinData;
}

const focusInput = ref();

function toggleIsFormVisible() {
    isFormVisible.value = !isFormVisible.value;
    if(isFormVisible.value) {
        nextTick(() => {
            focusInput.value?.focus()
        })
    }
}

const closeDropdown = () => {
    isDropdownVisible.value = false;
    isFormVisible.value = false;
}

function clearForm() {
    trayNameInput.value = '';
    trayDescriptionInput.value = '';
}

const submitCreateTray = () => {
    createTray(trayNameInput.value, trayDescriptionInput.value, 'icon-pin-tray');
    setTimeout(() => {
        clearForm();
        isFormVisible.value = false;
    }, 1200);
}

//ONMOUNTED
onMounted(() => {
    eventBus.on('pins:trays-fetched',onTraysFetched);
    eventBus.on('pins:tray-created',onTrayCreated);
    eventBus.on('pins:tray-updated',onTrayUpdated);
    eventBus.on('pins:tray-exporting',onTrayExporting);
    drawer.value.state.fetching = true;
    fetchTrays()
});

//ONUNMOUNTED
onUnmounted(() => {
    eventBus.off('pins:trays-fetched',onTraysFetched);
    eventBus.off('pins:tray-created',onTrayCreated);
    eventBus.off('pins:tray-updated',onTrayUpdated);
    eventBus.off('pins:tray-exporting',onTrayExporting);
});

// Pins Global State
const exporting = ref(false);

function onTraysFetched(trays) {
    drawer.value.state.fetching = false;
}

function onTrayCreated(tray) {
    drawer.value.state.fetching = true;
    fetchTrays()
}

function onTrayUpdated(tray) {
    drawer.value.state.fetching = true;
    fetchTrays()
}

function onTrayExporting(exporting_status) {
    exporting.value = exporting_status;
}
</script>
